import { useT } from "@magic-md/i18n/react";
import { LocateFixed, MessageSquare } from "lucide-react";
import { memo, type CSSProperties, type MouseEvent as ReactMouseEvent, type ReactNode, useRef, useState } from "react";
import {
    dispatchAnnotationBlockComment,
    dispatchAnnotationSelect,
} from "./events";
import { useAnnotationContextOptional } from "./AnnotationProvider";
import type { PreviewAnnotation } from "./types";

const EMPTY_ANNOTATIONS: PreviewAnnotation[] = [];

export function AnnotationBlockTarget({
    blockId,
    children,
}: {
    blockId: string;
    label?: string;
    children: ReactNode;
}) {
    const t = useT("editor");
    const context = useAnnotationContextOptional();
    const rootRef = useRef<HTMLDivElement>(null);
    const [dragStart, setDragStart] = useState<NormalizedPoint | null>(null);
    const [placementPreview, setPlacementPreview] = useState<PlacementPreview | null>(null);
    const isPlacing = context?.areaPlacement?.blockId === blockId;
    const areaAnnotations =
        context?.getRenderedAreaAnnotations(blockId) ?? EMPTY_ANNOTATIONS;

    if (!context) {
        return <>{children}</>;
    }

    const handlePlacementMouseDown = (event: ReactMouseEvent<HTMLDivElement>) => {
        if (!isPlacing) return;
        const rect = rootRef.current?.getBoundingClientRect();
        if (!rect) return;
        event.preventDefault();
        event.stopPropagation();
        const point = readNormalizedPoint(rect, event.clientX, event.clientY);
        setDragStart(point);
        setPlacementPreview({ kind: "point", point });
    };

    const handlePlacementMouseMove = (event: ReactMouseEvent<HTMLDivElement>) => {
        if (!isPlacing) return;
        const root = rootRef.current;
        const rect = root?.getBoundingClientRect();
        if (!root || !rect) return;
        const point = readNormalizedPoint(rect, event.clientX, event.clientY);
        if (dragStart) {
            setPlacementPreview({
                kind: "area",
                area: normalizeArea(dragStart, point),
            });
            return;
        }
        const semanticTarget = findSemanticTarget(root, event.clientX, event.clientY);
        if (semanticTarget) {
            const style = elementTargetStyleForElement(root, semanticTarget);
            if (style) {
                setPlacementPreview({
                    kind: "element",
                    style,
                    label: readElementLabel(semanticTarget),
                });
                return;
            }
        }
        setPlacementPreview({ kind: "point", point });
    };

    const handlePlacementMouseUp = (event: ReactMouseEvent<HTMLDivElement>) => {
        if (!isPlacing || !dragStart) return;
        const root = rootRef.current;
        const rect = root?.getBoundingClientRect();
        if (!root || !rect) return;
        event.preventDefault();
        event.stopPropagation();
        const end = readNormalizedPoint(rect, event.clientX, event.clientY);
        const area = normalizeArea(dragStart, end);
        if (area.shape === "point") {
            const semanticTarget = findSemanticTarget(root, event.clientX, event.clientY);
            if (semanticTarget) {
                const semanticRect = semanticTarget.getBoundingClientRect();
                context.selectRenderedElement({
                    blockId,
                    label: context.areaPlacement?.label,
                    outputHash: context.areaPlacement?.outputHash,
                    selector: buildSemanticSelector(root, semanticTarget),
                    elementLabel: readElementLabel(semanticTarget),
                    tagName: semanticTarget.tagName.toLowerCase(),
                    rect: {
                        left: semanticRect.left,
                        top: semanticRect.top,
                        bottom: semanticRect.bottom,
                    },
                });
                setDragStart(null);
                setPlacementPreview(null);
                return;
            }
        }
        context.selectRenderedArea({
            blockId,
            label: context.areaPlacement?.label,
            outputHash: context.areaPlacement?.outputHash,
            ...area,
            rect: areaToViewportRect(rect, area),
        });
        setDragStart(null);
        setPlacementPreview(null);
    };

    const handlePlacementMouseLeave = () => {
        if (dragStart) return;
        setPlacementPreview(null);
    };

    return (
        <div
            ref={rootRef}
            data-md-annotation-block-id={blockId}
            className="relative group/annotation-block"
        >
            {children}
            {areaAnnotations.map((annotation) => {
                if (
                    annotation.target.kind !== "rendered-area" &&
                    annotation.target.kind !== "rendered-element"
                ) {
                    return null;
                }
                const style = annotation.target.kind === "rendered-area"
                    ? areaTargetStyle(annotation.target)
                    : elementTargetStyle(rootRef.current, annotation.target.selector);
                if (!style) return null;
                return (
                    <button
                        key={annotation.id}
                        type="button"
                        aria-label={t("annotation.viewAreaComment")}
                        title={
                            annotation.target.kind === "rendered-element"
                                ? t("annotation.viewElementComment")
                                : t("annotation.viewAreaComment")
                        }
                        data-md-annotation-ids={annotation.id}
                        data-md-annotation-ui="rendered-area"
                        className="absolute z-20 rounded border border-blue-500/70 bg-blue-500/15 shadow-sm transition-colors hover:bg-blue-500/25"
                        style={style}
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            dispatchAnnotationSelect({
                                documentPath: context.documentPath,
                                annotationId: annotation.id,
                                openPopover: true,
                            });
                        }}
                    />
                );
            })}
            {isPlacing ? (
                <div
                    data-md-annotation-ui="rendered-area-placement"
                    className="absolute inset-0 z-30 cursor-crosshair rounded bg-primary/5 ring-1 ring-primary/30"
                    onMouseDown={handlePlacementMouseDown}
                    onMouseMove={handlePlacementMouseMove}
                    onMouseUp={handlePlacementMouseUp}
                    onMouseLeave={handlePlacementMouseLeave}
                >
                    {placementPreview ? (
                        <PlacementPreviewOverlay preview={placementPreview} />
                    ) : null}
                    <div className="pointer-events-none absolute right-2 top-2 rounded bg-background/95 px-2 py-1 text-[10px] text-muted-foreground shadow-sm">
                        {t("annotation.placementHint")}
                    </div>
                </div>
            ) : null}
        </div>
    );
}

function PlacementPreviewOverlay({ preview }: { preview: PlacementPreview }) {
    if (preview.kind === "element") {
        return (
            <div
                data-testid="annotation-placement-preview-element"
                className="pointer-events-none absolute z-10 rounded border border-primary/70 bg-primary/10 shadow-sm ring-2 ring-primary/15"
                style={preview.style}
            >
                {preview.label ? (
                    <span className="absolute -top-5 left-0 max-w-40 truncate rounded border border-border bg-popover px-1.5 py-0.5 text-[10px] text-popover-foreground shadow-sm">
                        {preview.label}
                    </span>
                ) : null}
            </div>
        );
    }
    if (preview.kind === "area") {
        return (
            <div
                data-testid="annotation-placement-preview-area"
                className="pointer-events-none absolute z-10 rounded border border-primary/70 bg-primary/10 shadow-sm"
                style={areaTargetStyle(preview.area)}
            />
        );
    }
    return (
        <div
            data-testid="annotation-placement-preview-point"
            className="pointer-events-none absolute z-10 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border border-primary/80 bg-primary/15 shadow-sm ring-4 ring-primary/10"
            style={{
                left: `${preview.point.x * 100}%`,
                top: `${preview.point.y * 100}%`,
            }}
        />
    );
}

export const AnnotationBlockFooterControl = memo(function AnnotationBlockFooterControl({
    blockId,
    label,
    outputHash,
}: {
    blockId: string;
    label?: string;
    outputHash?: string;
}) {
    const t = useT("editor");
    const context = useAnnotationContextOptional();
    const controlRef = useRef<HTMLDivElement>(null);
    const blockAnnotations =
        context?.getRenderedBlockAnnotations(blockId) ?? EMPTY_ANNOTATIONS;
    const isPlacing = context?.areaPlacement?.blockId === blockId;

    if (!context) return null;

    const openComposer = () => {
        const rect = controlRef.current?.getBoundingClientRect();
        if (!rect) return;
        dispatchAnnotationBlockComment({
            documentPath: context.documentPath,
            blockId,
            label,
            outputHash,
            rect: {
                left: rect.left,
                top: rect.top,
                bottom: rect.bottom,
            },
        });
    };

    const openExisting = () => {
        const first = blockAnnotations[0];
        if (!first) return;
        dispatchAnnotationSelect({
            documentPath: context.documentPath,
            annotationId: first.id,
            openPopover: true,
        });
    };

    return (
        <div
            ref={controlRef}
            className="flex items-center gap-1"
            data-md-annotation-ui="block-footer"
        >
            <FooterAnnotationButton
                title={isPlacing ? t("annotation.cancelLocationComment") : t("annotation.addLocationComment")}
                ariaLabel={isPlacing ? t("annotation.cancelLocationComment") : t("annotation.addLocationComment")}
                active={Boolean(isPlacing)}
                onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    context.startRenderedAreaPlacement({ blockId, label, outputHash });
                }}
            >
                <LocateFixed size={11} />
            </FooterAnnotationButton>
            <FooterAnnotationButton
                title={blockAnnotations.length > 0 ? t("annotation.viewBlockComment") : t("annotation.addBlockComment")}
                ariaLabel={blockAnnotations.length > 0 ? t("annotation.viewBlockComment") : t("annotation.addBlockComment")}
                minWidth
                onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    if (blockAnnotations.length > 0) {
                        openExisting();
                    } else {
                        openComposer();
                    }
                }}
            >
                <MessageSquare size={11} />
                {blockAnnotations.length > 0 ? (
                    <span className="ml-0.5 font-mono text-[10px]">{blockAnnotations.length}</span>
                ) : null}
            </FooterAnnotationButton>
        </div>
    );
});

function FooterAnnotationButton({
    title,
    ariaLabel,
    active,
    minWidth,
    children,
    onClick,
}: {
    title: string;
    ariaLabel: string;
    active?: boolean;
    minWidth?: boolean;
    children: ReactNode;
    onClick: (event: ReactMouseEvent<HTMLButtonElement>) => void;
}) {
    return (
        <button
            type="button"
            title={title}
            aria-label={ariaLabel}
            className={`flex h-5 ${minWidth ? "min-w-5 px-1" : "w-5"} items-center justify-center rounded transition-colors ${active
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
            onClick={onClick}
        >
            {children}
        </button>
    );
}

interface NormalizedPoint {
    x: number;
    y: number;
}

interface NormalizedArea {
    shape: "point" | "area";
    x: number;
    y: number;
    width: number;
    height: number;
}

type PlacementPreview =
    | {
        kind: "point";
        point: NormalizedPoint;
    }
    | {
        kind: "area";
        area: NormalizedArea;
    }
    | {
        kind: "element";
        style: CSSProperties;
        label?: string;
    };

function readNormalizedPoint(rect: DOMRect, clientX: number, clientY: number): NormalizedPoint {
    return {
        x: clamp01((clientX - rect.left) / Math.max(1, rect.width)),
        y: clamp01((clientY - rect.top) / Math.max(1, rect.height)),
    };
}

function normalizeArea(start: NormalizedPoint, end: NormalizedPoint): NormalizedArea {
    const left = Math.min(start.x, end.x);
    const top = Math.min(start.y, end.y);
    const width = Math.abs(end.x - start.x);
    const height = Math.abs(end.y - start.y);
    if (width < 0.025 && height < 0.025) {
        return {
            shape: "point",
            x: clamp01(end.x - 0.0125),
            y: clamp01(end.y - 0.0125),
            width: 0.025,
            height: 0.025,
        };
    }
    return {
        shape: "area",
        x: left,
        y: top,
        width: Math.max(0.025, width),
        height: Math.max(0.025, height),
    };
}

function areaToViewportRect(rect: DOMRect, area: NormalizedArea) {
    const left = rect.left + rect.width * area.x;
    const top = rect.top + rect.height * area.y;
    const height = Math.max(16, rect.height * area.height);
    return {
        left,
        top,
        bottom: top + height,
    };
}

function areaTargetStyle(target: {
    shape: "point" | "area";
    x: number;
    y: number;
    width: number;
    height: number;
}) {
    return {
        left: `${target.x * 100}%`,
        top: `${target.y * 100}%`,
        width: target.shape === "point" ? "12px" : `${target.width * 100}%`,
        height: target.shape === "point" ? "12px" : `${target.height * 100}%`,
        transform: target.shape === "point" ? "translate(-50%, -50%)" : undefined,
    };
}

function elementTargetStyle(root: HTMLElement | null, selector: string) {
    if (!root) return null;
    const element = findElementBySemanticSelector(root, selector);
    if (!element) return null;
    return elementTargetStyleForElement(root, element);
}

function elementTargetStyleForElement(root: HTMLElement, element: HTMLElement): CSSProperties | null {
    const rootRect = root.getBoundingClientRect();
    const rect = element.getBoundingClientRect();
    return {
        left: `${rect.left - rootRect.left}px`,
        top: `${rect.top - rootRect.top}px`,
        width: `${Math.max(12, rect.width)}px`,
        height: `${Math.max(12, rect.height)}px`,
    };
}

function findSemanticTarget(root: HTMLElement, clientX: number, clientY: number): HTMLElement | null {
    const elements = typeof root.ownerDocument.elementsFromPoint === "function"
        ? root.ownerDocument.elementsFromPoint(clientX, clientY)
        : typeof root.ownerDocument.elementFromPoint === "function"
            ? [root.ownerDocument.elementFromPoint(clientX, clientY)].filter(Boolean)
            : [];
    for (const element of elements) {
        if (!(element instanceof HTMLElement)) continue;
        if (!root.contains(element)) continue;
        if (element.dataset.mdAnnotationUi) continue;
        if (element === root) continue;
        const explicit = element.closest<HTMLElement>("[data-md-annotation-key],[data-md-annotation-label]");
        if (explicit && root.contains(explicit)) return explicit;
        if (["TD", "TH", "IMG", "SVG", "CANVAS", "PATH", "RECT", "CIRCLE", "TEXT"].includes(element.tagName)) {
            return element;
        }
    }
    return null;
}

function buildSemanticSelector(root: HTMLElement, element: HTMLElement): string {
    const explicitKey = element.dataset.mdAnnotationKey;
    if (explicitKey) return `key:${explicitKey}`;
    const parts: string[] = [];
    let current: HTMLElement | null = element;
    while (current && current !== root) {
        const parent: HTMLElement | null = current.parentElement;
        if (!parent) break;
        const sameTagSiblings = Array.from(parent.children)
            .filter((child): child is HTMLElement =>
                child instanceof HTMLElement && child.tagName === current?.tagName,
            );
        const index = Math.max(0, sameTagSiblings.indexOf(current));
        parts.push(`${current.tagName.toLowerCase()}:nth-of-type(${index + 1})`);
        current = parent;
    }
    return `path:${parts.reverse().join(">")}`;
}

function findElementBySemanticSelector(root: HTMLElement, selector: string): HTMLElement | null {
    if (selector.startsWith("key:")) {
        return root.querySelector<HTMLElement>(
            `[data-md-annotation-key="${cssAttributeValue(selector.slice(4))}"]`,
        );
    }
    if (!selector.startsWith("path:")) return null;
    try {
        return root.querySelector<HTMLElement>(selector.slice(5));
    } catch {
        return null;
    }
}

function readElementLabel(element: HTMLElement): string {
    const explicit = element.dataset.mdAnnotationLabel || element.getAttribute("aria-label") || element.getAttribute("title");
    if (explicit) return explicit.trim();
    if (element instanceof HTMLImageElement) return element.alt || element.src.split("/").pop() || "image";
    const text = element.textContent?.replace(/\s+/g, " ").trim();
    if (text) return text.length > 40 ? `${text.slice(0, 39)}…` : text;
    return element.tagName.toLowerCase();
}

function cssAttributeValue(value: string): string {
    return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

function clamp01(value: number): number {
    return Math.min(1, Math.max(0, value));
}
